/* ShinShield brand scripts: small, dependency-free helpers shared by the ss-* sections. */

const ssReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Buttons with data-ss-scroll-to="id" scroll to that section when it is on the page,
   otherwise they behave as normal links (e.g. to the product page). */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-ss-scroll-to]');
  if (!link) return;
  const target = document.getElementById(link.dataset.ssScrollTo);
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: ssReducedMotion() ? 'instant' : 'smooth', block: 'start' });
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  // Bundle links pre-select that bundle in the buy section
  if (link.dataset.ssBundle) {
    document.dispatchEvent(new CustomEvent('ss:bundle-select', { detail: { qty: Number(link.dataset.ssBundle) } }));
  }
});

/* Announcement ticker: pause/play button (WCAG 2.2.2) on top of hover/focus pausing. */
class SsTicker extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('.ss-ticker__toggle');
    button?.addEventListener('click', () => {
      const paused = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', paused ? 'Play announcements' : 'Pause announcements');
      this.classList.toggle('is-paused', paused);
    });
  }
}

if (!customElements.get('ss-ticker')) customElements.define('ss-ticker', SsTicker);

/* Colour preview card: swatches swap the product photo. Optionally cycles through colours
   while visible, and stops for good as soon as the shopper picks one. */
class SsColourPicker extends HTMLElement {
  connectedCallback() {
    this.swatches = [...this.querySelectorAll('[data-ss-colour]')];
    this.images = [...this.querySelectorAll('.ss-picker__img')];
    this.nameEl = this.querySelector('[data-ss-colour-name]');
    this.index = 0;

    this.swatches.forEach((swatch) =>
      swatch.addEventListener('click', () => {
        this.stop();
        this.show(Number(swatch.dataset.ssColour));
      })
    );

    if (this.dataset.autoplay === 'true' && !ssReducedMotion() && this.swatches.length > 1) {
      this.observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.start() : this.pause()), {
        threshold: 0.4,
      });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.stop();
  }

  show(index) {
    this.index = index;
    this.swatches.forEach((swatch, i) => swatch.setAttribute('aria-pressed', String(i === index)));
    this.images.forEach((image, i) => (image.dataset.active = String(i === index)));
    if (this.nameEl) this.nameEl.textContent = this.swatches[index]?.dataset.name ?? '';
  }

  start() {
    if (this.timer || this.stopped) return;
    this.timer = setInterval(() => this.show((this.index + 1) % this.swatches.length), 2600);
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  stop() {
    this.stopped = true;
    this.pause();
    this.observer?.disconnect();
  }
}

if (!customElements.get('ss-colour-picker')) customElements.define('ss-colour-picker', SsColourPicker);

/* Scroll reveal: elements with [data-ss-reveal] fade up once when they enter the viewport.
   The hidden start state only applies when <html> has .ss-js, so nothing hides without JS. */
(() => {
  const els = document.querySelectorAll('[data-ss-reveal]');
  if (!('IntersectionObserver' in window) || ssReducedMotion()) {
    els.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      }),
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );
  const observe = (root = document) => root.querySelectorAll('[data-ss-reveal]:not(.is-visible)').forEach((el) => io.observe(el));
  observe();
  // Theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
})();

/* Swipeable strip (native scroll-snap). Arrow buttons, counter and progress live anywhere
   inside the same <section>: [data-ss-prev] [data-ss-next] [data-ss-current] [data-ss-progress]. */
class SsCarousel extends HTMLElement {
  connectedCallback() {
    const scope = this.closest('section') || this;
    this.track = this.querySelector('[data-ss-track]');
    this.slides = [...this.track.children];
    this.prev = scope.querySelector('[data-ss-prev]');
    this.next = scope.querySelector('[data-ss-next]');
    this.current = scope.querySelector('[data-ss-current]');
    this.progress = scope.querySelector('[data-ss-progress]');

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      }
    });
    this.setupDrag();
    this.onScroll = () => requestAnimationFrame(() => this.update());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onScroll);
  }

  /* Click-and-drag with a mouse (touch and trackpads already swipe natively).
     Snap is paused while dragging, then the strip settles on the nearest card. */
  setupDrag() {
    const track = this.track;
    let drag = null;
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false, id: event.pointerId };
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 5) return;
      if (!drag.moved) {
        drag.moved = true;
        track.setPointerCapture(drag.id);
        track.classList.add('is-dragging');
      }
      track.scrollLeft = drag.left - dx;
    });
    const end = () => {
      if (!drag) return;
      const { moved, left } = drag;
      drag = null;
      if (!moved) return;
      const step = this.step();
      const moveBy = track.scrollLeft - left;
      // A short flick still moves one card in that direction
      const target = Math.abs(moveBy) > step * 0.15 && Math.abs(moveBy) < step * 0.5
        ? left + Math.sign(moveBy) * step
        : Math.round(track.scrollLeft / step) * step;
      track.classList.remove('is-dragging');
      track.scrollTo({ left: target, behavior: ssReducedMotion() ? 'instant' : 'smooth' });
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', end);
  }

  step() {
    const [a, b] = this.slides;
    return b ? b.offsetLeft - a.offsetLeft : this.track.clientWidth;
  }

  go(direction) {
    this.track.scrollBy({ left: direction * this.step(), behavior: ssReducedMotion() ? 'instant' : 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    // At the end of the strip the last few cards share the view, so count the last one as current
    const index = scrollLeft >= max - 2 ? this.slides.length - 1 : Math.min(this.slides.length - 1, Math.round(scrollLeft / this.step()));
    if (this.current) this.current.textContent = String(index + 1);
    if (this.progress) this.progress.style.transform = `scaleX(${max > 0 ? (scrollLeft + clientWidth) / scrollWidth : 1})`;
    if (this.prev) this.prev.disabled = scrollLeft <= 2;
    if (this.next) this.next.disabled = scrollLeft >= max - 2;
  }
}

if (!customElements.get('ss-carousel')) customElements.define('ss-carousel', SsCarousel);

/* Looping section videos: respect reduced motion, and only play while on screen. */
(() => {
  const videos = () => document.querySelectorAll('video[data-ss-autoplay]');
  if (ssReducedMotion()) {
    const stop = () => videos().forEach((video) => { video.removeAttribute('autoplay'); video.pause(); });
    stop();
    document.addEventListener('shopify:section:load', stop);
    return;
  }
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) =>
    entries.forEach(({ target, isIntersecting }) => (isIntersecting ? target.play().catch(() => {}) : target.pause()))
  );
  const watch = () => videos().forEach((video) => io.observe(video));
  watch();
  document.addEventListener('shopify:section:load', watch);
})();

/* Links to a collapsed FAQ answer (e.g. #size-swap) open it, scroll to it and move focus there.
   Works for same-page links and when arriving from another page with the hash in the URL. */
(() => {
  const openAnswer = (id, smooth) => {
    const target = id && document.getElementById(id);
    if (!target || target.tagName !== 'DETAILS') return false;
    target.open = true;
    target.scrollIntoView({ behavior: smooth && !ssReducedMotion() ? 'smooth' : 'instant', block: 'start' });
    target.querySelector('summary')?.focus({ preventScroll: true });
    return true;
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || link.hasAttribute('data-ss-scroll-to')) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname || !url.hash) return;
    if (openAnswer(decodeURIComponent(url.hash.slice(1)), true)) {
      event.preventDefault();
      history.replaceState(null, '', url.hash);
    }
  });

  const fromUrl = () => openAnswer(decodeURIComponent(window.location.hash.slice(1)), false);
  if (document.readyState === 'complete') fromUrl();
  else window.addEventListener('load', fromUrl);
})();
